import React, { useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { AttentionMessage, Box, AnimatedSpinner, Center } from '@tq/ui-components';

import { AppBar, AppFooter } from 'components';
import { useNavigate, useNavigation } from 'hooks';
import { usePermissions } from './hooks';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('layout');
  const location = useLocation();

  // MSAL hooks to handle initial load / hard refresh state safely
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const { hasPageViewPermissions, isFetchingPermissions } = usePermissions();
  const { isBreadcrumbPage, isLoginPage } = useNavigation(location.pathname);
  const { navigateToHome } = useNavigate();
  const layoutTopPadding = isBreadcrumbPage ? 154 : 96;

  // scroll to top on path change
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);

  // Redirect authenticated users away from login page
  useLayoutEffect(() => {
    if (inProgress === InteractionStatus.None && isLoginPage && isAuthenticated) {
      navigateToHome();
    }
  }, [isAuthenticated, navigateToHome, isLoginPage, inProgress]);

  // Safe accessibility shortcut listener effect
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() === 'h') {
        const target = event.target as HTMLElement;
        if (
          target.tagName &&
          target.tagName !== 'INPUT' &&
          target.tagName !== 'TEXTAREA' &&
          !target.isContentEditable
        ) {
          event.preventDefault();
          const mainElement = document.getElementById('main');
          if (mainElement) {
            if (!mainElement.hasAttribute('tabindex')) {
              mainElement.setAttribute('tabindex', '-1');
            }
            mainElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Explicit cleanup function wrapper to prevent unmount crashes
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // If MSAL is actively initialising tokens (especially on initial load/refresh),
  // gracefully wait instead of triggering a fallback route crash.
  if (inProgress !== InteractionStatus.None) {
    return (
      <Center height='100vh'>
        <AnimatedSpinner size='lg' />
      </Center>
    );
  }

  return (
    <>
      <AppBar location={location.pathname} isAuthenticated={isAuthenticated} />
      <Box
        pt={{ base: 24, lg: layoutTopPadding }}
        mx={{ base: 5, lg: 0 }}
        minHeight='calc(100vh - 260px)'
      >
        <Box id='main' mt={{ base: 0, lg: 8 }}>
          {hasPageViewPermissions ? (
            children
          ) : (
            <Center>
              {isFetchingPermissions ? (
                <AnimatedSpinner size='lg' />
              ) : (
                <Box
                  position='absolute'
                  top={{ base: '90px', xl: `${24 + layoutTopPadding}px` }}
                >
                  <AttentionMessage
                    status='error'
                    description={t(
                      'no_permission',
                      "You don't have permission to access this page"
                    )}
                  />
                </Box>
              )}
            </Center>
          )}
        </Box>
      </Box>
      <AppFooter />
    </>
  );
};
